import { useState, useCallback, useRef, useEffect } from 'react';
import Header from '../homepage/Header';
import DocsLeftNav from './DocsLeftNav';
import DocsContent from './DocsContent';
import DocsOnThisPage from './DocsOnThisPage';

function SunIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
      <circle cx="12" cy="12" r="4" />
      <path strokeLinecap="round" d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  );
}

function MobileNavToggle({ isDark, isOpen, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="
        md:hidden fixed bottom-6 right-6 z-40 rounded-full w-12 h-12
        flex items-center justify-center shadow-2xl
        bg-gray-900 text-white
        dark:bg-brand-50 dark:text-brand-900
      "
      aria-label="Toggle navigation"
    >
      {isOpen ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      )}
    </button>
  );
}

const STORAGE_KEY = 'pabs-docs-theme';

export default function DocsLayout({ slug }) {
  const [headings, setHeadings] = useState([]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved !== null ? saved === 'dark' : true;
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({top: 0});
    setMobileNavOpen(false);
  }, [slug]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');
  }, [isDark]);

  const handleHeadingsExtracted = useCallback((extracted) => {
    setHeadings(extracted);
  }, []);

  // Outer wrapper carries the 'dark' class - enables all dark: variants inside
  return (
    <div className={isDark ? 'dark' : ''}>
      <div className="h-screen flex flex-col font-sans overflow-hidden bg-white dark:bg-brand-900">

        {/* Grid texture - dark only */}
        {isDark && (
          <div
            className="fixed inset-0 pointer-events-none z-0 opacity-30"
            style={{
              backgroundImage:
                'linear-gradient(to right, #ffffff05 1px, transparent 1px), linear-gradient(to bottom, #ffffff05 1px, transparent 1px)',
              backgroundSize: '80px 80px',
            }}
          />
        )}

        {/* Header */}
        <div className="relative z-10">
          <Header embedded utilities={<button type="button" onClick={() => setIsDark(value => !value)} aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} className="flex h-8 w-8 items-center justify-center rounded-full text-brand-700 hover:bg-brand-200">{isDark ? <SunIcon/> : <MoonIcon/>}</button>}/>
        </div>

        {/* Body row */}
        <div className="relative z-10 flex flex-1 overflow-hidden">

          {/* Left sidebar */}
          <aside className="hidden md:flex flex-col w-[240px] shrink-0 overflow-y-auto border-r border-gray-200 dark:border-brand-50/10">
            <DocsLeftNav />
          </aside>

          {/* Mobile overlay */}
          {mobileNavOpen && (
            <div
              className="md:hidden absolute inset-0 z-30 overflow-y-auto bg-white dark:bg-brand-900"
              onClick={(e) => { if (e.target.tagName === 'A') setMobileNavOpen(false); }}
            >
              <div className="px-6 py-6">
                <DocsLeftNav />
              </div>
            </div>
          )}

          {/* Center - only scrolling column */}
          <main ref={scrollRef} className="min-w-0 flex-1 overflow-y-auto px-6 md:px-12 py-12">
            <DocsContent slug={slug} onHeadingsExtracted={handleHeadingsExtracted} />
          </main>

          {/* Right sidebar */}
          {headings.length > 0 && (
            <aside className="hidden xl:flex flex-col w-[200px] shrink-0 overflow-y-auto border-l border-gray-200 dark:border-brand-50/10">
              <DocsOnThisPage headings={headings} scrollRoot={scrollRef} />
            </aside>
          )}
        </div>

        <MobileNavToggle isDark={isDark} isOpen={mobileNavOpen} onToggle={() => setMobileNavOpen(v => !v)} />
      </div>
    </div>
  );
}
